import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { CONTENT_LABEL_BUCKET_NAME } from '../utils/contentLabelBucket';

import { LabelChip } from './LabelChip';

/**
 * One content-label value as an aggregate table row reads it: the category chip
 * when the API sent a value, and the ONE null bucket when it did not. Used by
 * the Performance group header, the Optimize group cell and the Filters drawer's
 * pseudo-row; `contentLabelBucket.ts` owns the name and every colour.
 */

// The hexes appear literally because Tailwind's JIT scans source text and
// cannot see a runtime-built class name.
const bucketText = cva('truncate font-medium leading-[1.45] tracking-[0.26px]', {
  variants: {
    surface: {
      light: 'text-[13px] text-[#41424E]',
      dark: 'text-[14px] text-[#A6A7B5]',
    },
  },
  defaultVariants: { surface: 'light' },
});

type ContentLabelValueProps = VariantProps<typeof bucketText> & {
  /**
   * The category value's name. `null` AND `undefined` both mean the one null
   * bucket — some shapes omit the field rather than sending null, and a caller
   * should not have to normalise that before rendering.
   */
  value?: string | null;
  /** The owning category's hex colour, used only when `value` is a real label. */
  categoryColor?: string;
  className?: string;
};

export function ContentLabelValue({
  value,
  categoryColor,
  surface = 'light',
  className,
}: ContentLabelValueProps): JSX.Element {
  if (value != null) {
    return (
      <LabelChip categoryColor={categoryColor ?? ''} className={className}>
        {value}
      </LabelChip>
    );
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center', className)}>
      <span className={bucketText({ surface })}>{CONTENT_LABEL_BUCKET_NAME}</span>
    </span>
  );
}
